import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { formatDateTimeSaoPaulo } from "../../lib/currency";

interface LibraryItem {
  id: string;
  catalog_item_id: string;
  title: string;
  slug: string;
  granted_at: string;
  expires_at: string | null;
  revoked_at: string | null;
}

export function MyLibraryPage() {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<{ items: LibraryItem[] }>("/consumer/library")
      .then((data) => setItems(data.items))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Não foi possível carregar sua biblioteca."))
      .finally(() => setLoading(false));
  }, []);

  async function download(catalogItemId: string, format: "pdf" | "epub") {
    try {
      const result = await apiClient.post<{ downloadUrl: string }>("/consumer/library/download-token", { catalogItemId, format });
      window.location.href = result.downloadUrl;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Download não autorizado.");
    }
  }

  if (loading) return <p className="text-muted">Carregando…</p>;
  if (error) {
    const isAuthRequired = error.toLowerCase().includes("login") || error.toLowerCase().includes("unauthenticated");
    if (isAuthRequired) {
      return (
        <div className="card" style={{ maxWidth: 520, margin: "var(--space-6) auto", textAlign: "center", padding: "var(--space-6)" }}>
          <h2>Acesso à Minha Biblioteca</h2>
          <p className="text-muted" style={{ margin: "var(--space-3) 0 var(--space-5)" }}>
            Faça login com sua conta de leitor para acessar seus livros digitais adquiridos e realizar o download dos formatos PDF e EPUB.
          </p>
          <div style={{ display: "flex", gap: "var(--space-3)", justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/login?redirect=/minha-biblioteca" className="btn">
              Fazer Login
            </a>
            <a href="/catalogo" className="btn btn-secondary">
              Explorar Catálogo
            </a>
          </div>
        </div>
      );
    }
    return <p role="alert">{error}</p>;
  }

  return (
    <div>
      <h1>Minha biblioteca</h1>
      {items.length === 0 ? (
        <p className="empty-state">
          Você ainda não possui livros. Explore o <a href="/catalogo">catálogo</a>.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {items.map((item) => {
            const isExpired = item.expires_at && new Date(item.expires_at) < new Date();
            return (
              <li key={item.id} className="card">
                <strong>{item.title}</strong>
                <p className="text-muted" style={{ fontSize: "var(--text-sm)" }}>
                  Adquirido em {formatDateTimeSaoPaulo(item.granted_at)}
                  {item.expires_at && ` — acesso até ${formatDateTimeSaoPaulo(item.expires_at)}`}
                </p>
                {isExpired || item.revoked_at ? (
                  <p className="text-muted">Downloads bloqueados (histórico mantido).</p>
                ) : (
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <button className="btn btn-secondary" onClick={() => download(item.catalog_item_id, "pdf")}>
                      Baixar PDF
                    </button>
                    <button className="btn btn-secondary" onClick={() => download(item.catalog_item_id, "epub")}>
                      Baixar EPUB
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
